import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiArrowUpRight, FiCalendar } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "react-query";
import { getPatientAppointmentsApi } from "../../../../api/appointments";
import CommentAndTreatmentView from "../../../appointment/sections/ContinuatioSheet/CommentAndTreatmentView";

const formatDate = (value, includeTime = true) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat(
    "en-NG",
    includeTime
      ? { dateStyle: "medium", timeStyle: "short" }
      : { dateStyle: "long" },
  ).format(date);
};

const workerName = (worker) => {
  if (!worker || typeof worker === "string") return "Not available";
  return (
    `${worker.firstName || ""} ${worker.lastName || ""}`.trim() ||
    "Unknown worker"
  );
};

const ContinuationSheet = ({ patientData }) => {
  const legacyModal = useDisclosure();
  const [legacyAssessment, setLegacyAssessment] = useState(null);
  const query = useQuery(
    ["patientAttendanceHistory", patientData?._id],
    () => getPatientAppointmentsApi(patientData._id),
    { enabled: Boolean(patientData?._id) },
  );
  const appointments = query.data?.data?.appointments || [];
  const assessmentCount = appointments.reduce(
    (count, appointment) => count + (appointment.assessments?.length || 0),
    0,
  );
  const legacy = patientData?.continuationSheet?.commentAndTreatment || [];

  if (query.isLoading) {
    return (
      <Stack spacing="4">
        <Skeleton h="112px" borderRadius="xl" />
        <Skeleton h="112px" borderRadius="xl" />
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <Center minH="240px" flexDirection="column" textAlign="center">
        <Heading size="sm">Attendance history could not be loaded</Heading>
        <Text mt="2" color="gray.500" fontSize="sm">
          Please check your connection and try again.
        </Text>
        <Button
          mt="4"
          size="sm"
          variant="outline"
          onClick={() => query.refetch()}
        >
          Try again
        </Button>
      </Center>
    );
  }

  return (
    <Box>
      <Flex
        mb="5"
        justify="space-between"
        gap="4"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
      >
        <Box>
          <Heading size="md">Attendance history</Heading>
          <Text mt="1" color="gray.500" fontSize="sm">
            Every attendance for this patient, with its clinical assessments and
            responsible worker.
          </Text>
        </Box>
        <HStack spacing="2">
          <Badge colorScheme="blue" borderRadius="full" px="2">
            {appointments.length} attendances
          </Badge>
          <Badge variant="outline" borderRadius="full" px="2">
            {assessmentCount} assessments
          </Badge>
        </HStack>
      </Flex>

      {!appointments.length ? (
        <Center
          minH="180px"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="xl"
          flexDirection="column"
          textAlign="center"
        >
          <FiCalendar size="22" />
          <Text mt="3" fontWeight="700">
            No attendance history yet
          </Text>
          <Text mt="1" color="gray.500" fontSize="sm">
            The patient’s attendances will appear here.
          </Text>
        </Center>
      ) : (
        <Accordion
          allowMultiple
          defaultIndex={appointments.length === 1 ? [0] : []}
        >
          <Stack spacing="3">
            {appointments.map((appointment) => {
              const assessments = appointment.assessments || [];
              const attendingWorker =
                appointment.currentAttendingWorker ||
                appointment.lastAttendingWorker;
              return (
                <AccordionItem
                  key={appointment._id}
                  borderWidth="1px"
                  borderRadius="xl"
                  overflow="hidden"
                  bg="white"
                >
                  <AccordionButton
                    px={{ base: "4", md: "5" }}
                    py="4"
                    _hover={{ bg: "gray.50" }}
                  >
                    <Flex
                      flex="1"
                      minW="0"
                      justify="space-between"
                      gap="4"
                      align={{ base: "flex-start", md: "center" }}
                      direction={{ base: "column", md: "row" }}
                      textAlign="left"
                    >
                      <Box>
                        <HStack spacing="2" flexWrap="wrap">
                          <Text fontWeight="800">
                            Attendance on{" "}
                            {formatDate(
                              appointment.timestamp || appointment.createdAt,
                              false,
                            )}
                          </Text>
                          <Badge
                            colorScheme={appointment.closed ? "gray" : "green"}
                          >
                            {appointment.closed ? "Closed" : "Active"}
                          </Badge>
                        </HStack>
                        <Text mt="1" color="gray.500" fontSize="sm">
                          Attended by {workerName(attendingWorker)}
                        </Text>
                      </Box>
                      <Badge variant="subtle" colorScheme="blue">
                        {assessments.length}{" "}
                        {assessments.length === 1
                          ? "assessment"
                          : "assessments"}
                      </Badge>
                    </Flex>
                    <AccordionIcon ml="3" />
                  </AccordionButton>

                  <AccordionPanel px={{ base: "4", md: "5" }} pb="5">
                    <Divider mb="4" />
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap="3" mb="5">
                      <Box bg="gray.50" p="3" borderRadius="lg">
                        <Text color="gray.500" fontSize="xs" fontWeight="700">
                          OPENED
                        </Text>
                        <Text mt="1" fontSize="sm" fontWeight="600">
                          {formatDate(
                            appointment.timestamp || appointment.createdAt,
                          )}
                        </Text>
                      </Box>
                      <Box bg="gray.50" p="3" borderRadius="lg">
                        <Text color="gray.500" fontSize="xs" fontWeight="700">
                          CLOSED
                        </Text>
                        <Text mt="1" fontSize="sm" fontWeight="600">
                          {appointment.closed
                            ? formatDate(
                                appointment.closedAt || appointment.updatedAt,
                              )
                            : "Still active"}
                        </Text>
                      </Box>
                      <Box bg="gray.50" p="3" borderRadius="lg">
                        <Text color="gray.500" fontSize="xs" fontWeight="700">
                          ATTENDING WORKER
                        </Text>
                        <Text mt="1" fontSize="sm" fontWeight="600">
                          {workerName(attendingWorker)}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Flex mb="4" justify="space-between" align="center">
                      <Heading size="xs">CLINICAL ASSESSMENTS</Heading>
                      <Button
                        as={RouterLink}
                        to={`/appointment/${appointment._id}`}
                        size="sm"
                        variant="outline"
                        rightIcon={<FiArrowUpRight />}
                      >
                        View attendance
                      </Button>
                    </Flex>

                    {!assessments.length ? (
                      <Box
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderRadius="lg"
                        p="4"
                      >
                        <Text color="gray.500" fontSize="sm">
                          No clinical assessment was recorded for this
                          attendance.
                        </Text>
                      </Box>
                    ) : (
                      <Stack spacing="3">
                        {assessments.map((item, index) => (
                          <Box
                            key={item._id || index}
                            borderWidth="1px"
                            borderRadius="lg"
                            p="4"
                          >
                            <Flex
                              mb="3"
                              justify="space-between"
                              gap="3"
                              align={{ base: "flex-start", sm: "center" }}
                              direction={{ base: "column", sm: "row" }}
                            >
                              <Text fontWeight="800">
                                Assessment {index + 1}
                              </Text>
                              <Text color="gray.500" fontSize="xs">
                                {formatDate(item.createdAt)} ·{" "}
                                {workerName(item.createdBy)}
                              </Text>
                            </Flex>
                            <SimpleGrid columns={{ base: 1, lg: 3 }} gap="4">
                              <Box>
                                <Text
                                  fontSize="xs"
                                  fontWeight="800"
                                  color="gray.500"
                                >
                                  SIGNS AND SYMPTOMS
                                </Text>
                                <Text
                                  mt="1"
                                  fontSize="sm"
                                  whiteSpace="pre-wrap"
                                >
                                  {item.signsAndSymptoms || "Not recorded"}
                                </Text>
                              </Box>
                              <Box>
                                <Text
                                  fontSize="xs"
                                  fontWeight="800"
                                  color="gray.500"
                                >
                                  ASSESSMENT
                                </Text>
                                <Text
                                  mt="1"
                                  fontSize="sm"
                                  whiteSpace="pre-wrap"
                                >
                                  {item.assessment || "Not recorded"}
                                </Text>
                              </Box>
                              <Box>
                                <Text
                                  fontSize="xs"
                                  fontWeight="800"
                                  color="gray.500"
                                >
                                  PLAN
                                </Text>
                                <Text
                                  mt="1"
                                  fontSize="sm"
                                  whiteSpace="pre-wrap"
                                >
                                  {item.plan || "Not recorded"}
                                </Text>
                              </Box>
                            </SimpleGrid>
                            {!!item.diagnoses?.length && (
                              <HStack mt="4" flexWrap="wrap">
                                {item.diagnoses.map((diagnosis) => (
                                  <Badge
                                    key={diagnosis._id || diagnosis.name}
                                    colorScheme={
                                      diagnosis.verificationStatus ===
                                      "confirmed"
                                        ? "green"
                                        : "yellow"
                                    }
                                  >
                                    {diagnosis.name} ·{" "}
                                    {diagnosis.verificationStatus}
                                  </Badge>
                                ))}
                              </HStack>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Stack>
        </Accordion>
      )}

      {!!legacy.length && (
        <Box mt="7">
          <HStack mb="3">
            <Heading size="sm">Legacy patient assessments</Heading>
            <Badge colorScheme="gray">Attendance unknown</Badge>
          </HStack>
          <Text mb="3" color="gray.500" fontSize="sm">
            These records predate appointment-linked assessments, so their
            original attendance and author cannot be determined safely.
          </Text>
          <Stack spacing="2">
            {legacy.map((item, index) => (
              <Flex
                key={item._id || index}
                borderWidth="1px"
                borderRadius="lg"
                p="3"
                justify="space-between"
                align="center"
                gap="3"
              >
                <Box minW="0">
                  <Text fontWeight="700">
                    Historical assessment {index + 1}
                  </Text>
                  <Text color="gray.500" fontSize="sm" noOfLines={1}>
                    {item.assessment || item.signsAndSymptoms || "No summary"}
                  </Text>
                </Box>
                <Button
                  flexShrink="0"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setLegacyAssessment(item);
                    legacyModal.onOpen();
                  }}
                >
                  View
                </Button>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
      <CommentAndTreatmentView comment={legacyAssessment} modal={legacyModal} />
    </Box>
  );
};

export default ContinuationSheet;
